import type { SourceId } from './sources.js';

/**
 * OUVRIR LE CENTRE SUR UNE SOURCE OU UN SUJET, depuis ailleurs dans le shell
 * (la cloche du dock qui ouvre une « Reponse de Lia prete »). Un evenement de
 * fenetre plutot qu'une prop : la cloche vit dans le dock, le centre a la
 * racine, et ni l'un ni l'autre ne doit remonter l'etat de l'autre.
 */
export const EVENEMENT_OUVRIR_CENTRE = 'taki:centre:ouvrir';

export interface OuvrirCentre {
  source: SourceId;
  /**
   * Le `conversationId` d'un sujet de Lia (notification `lia`), ou l'id d'une
   * conversation du socle (notification `citation`, objet `conversation`) : le
   * centre lit alors son type pour choisir le groupe (canal, direct, chatter).
   */
  conversationId?: string;
  /** Le message a mettre en avant dans la conversation. */
  messageId?: string;
  /**
   * LE CLIC SUR UNE MENTION (essai du soir, ecart 1 ; emis par
   * `TexteAvecMentions` du socle) : `{ source: 'directs', personId }` ouvre le
   * message direct avec cette personne, cree s'il n'existe pas encore.
   */
  personId?: string;
  /**
   * LE GESTE D'UN LANCEUR (mascotte, « Ask Lia », cloche du dock) : ouvre la
   * source, mais FERME la fenetre si elle la montre deja (`gesteDuLanceur`).
   */
  lanceur?: true;
  /**
   * LA MASCOTTE REPREND (P9 du 06/10/2026) : fenetre fermee, elle la rouvre
   * sur la DERNIERE conversation regardee (memorisee par personne et par
   * poste, `etatCentre.ts`), jamais sur un sujet Lia neuf. Fenetre ouverte,
   * elle reste le lanceur de Lia (`gesteDuLanceur`).
   */
  reprendre?: true;
  /**
   * LE CHATTER D'UN OBJET (source `ici`) : la fiche `{ family, recordId }`, ou
   * la porte ou l'app `{ appId, doorId? }`. C'est l'entree que le bouton du
   * gabarit sur une fiche declenche (chantier chatter) :
   * `ouvrirCentre({ source: 'ici', ancre: { family: 'contacts', recordId } })`.
   * Le centre s'ouvre, lit `chatterDe(ancre)`, et cree le chatter au premier
   * message si aucun n'existe.
   */
  ancre?: AncreDemandee;
}

/**
 * L'ancre telle que le bouton « Chatter » du gabarit l'envoie (chantier
 * chatter, `packages/app-runtime/src/chatter.ts`) : la fiche
 * `{ family, recordId, appId?, doorId?, titre? }`, la porte
 * `{ appId, doorId, titre? }` ou l'app `{ appId, titre? }`. Le titre ne sert
 * qu'a l'ecran ; le SDK recoit l'ancre STRICTE (`ancreStricte`).
 */
export type AncreDemandee =
  | { family: string; recordId: string; appId?: string; doorId?: string; titre?: string }
  | { appId: string; doorId?: string; titre?: string };

/** L'ancre que `chatterDe` accepte : `{ family, recordId }`, `{ appId, doorId }` ou `{ appId }`, rien d'autre. */
export function ancreStricte(ancre: AncreDemandee): { family: string; recordId: string } | { appId: string; doorId?: string } {
  if ('family' in ancre) return { family: ancre.family, recordId: ancre.recordId };
  if (ancre.doorId) return { appId: ancre.appId, doorId: ancre.doorId };
  return { appId: ancre.appId };
}

/** La cle du chatter, la meme que `cleDeChatter` du data-model : `fiche:...`, `porte:...`, `app:...`. */
export function cleDuChatter(ancre: AncreDemandee): string {
  const a = ancreStricte(ancre);
  if ('family' in a) return `fiche:${a.family}/${a.recordId}`;
  return a.doorId ? `porte:${a.appId}/${a.doorId}` : `app:${a.appId}`;
}

/** Apres un envoi dans un chatter : le bouton « Chatter » de l'objet relit son compte (chantier chatter). */
export const EVENEMENT_CHATTER_ECRIT = 'taki:chatter:ecrit';

export function signalerChatterEcrit(ancre: AncreDemandee): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<{ cle: string }>(EVENEMENT_CHATTER_ECRIT, { detail: { cle: cleDuChatter(ancre) } }));
}

export function ouvrirCentre(demande: OuvrirCentre): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<OuvrirCentre>(EVENEMENT_OUVRIR_CENTRE, { detail: demande }));
}

/** Le `message=` d'un chemin de notification `citation` (`/communication?conversation=...&message=...`). */
export function messageDuChemin(chemin: string): string | undefined {
  const index = chemin.indexOf('?');
  if (index < 0) return undefined;
  const valeur = new URLSearchParams(chemin.slice(index + 1)).get('message');
  return valeur && /^[A-Za-z0-9_-]{1,128}$/.test(valeur) ? valeur : undefined;
}

/**
 * La destination d'une notification ouverte hors de la fenetre (lien d'origine
 * d'une ligne du Journal, porte « Notifications ») : la fenetre pour un sujet
 * de Lia (`lia`) ou une conversation du socle (`conversation`, rubrique
 * `citation`, contrat conversations section 5), son adresse sinon. Pour une
 * conversation, la source est choisie par la fenetre apres lecture du type ;
 * `canaux` est un point de depart. Le popover de la cloche, son premier
 * usage, est retire (R7 du soir) ; la fenetre aiguille ses propres lignes.
 */
export function destinationDeNotification(chemin: string, objet: { type: string; id: string } | null): { centre: OuvrirCentre } | { chemin: string } | null {
  if (objet?.type === 'lia') return { centre: { source: 'lia', conversationId: objet.id } };
  if (objet?.type === 'conversation') {
    const messageId = messageDuChemin(chemin);
    return { centre: { source: 'canaux', conversationId: objet.id, ...(messageId ? { messageId } : {}) } };
  }
  return chemin ? { chemin } : null;
}

/**
 * LES LANCEURS DE LA FENETRE (decisions du 05/10/2026, second essai et R7 du
 * soir) : la mascotte et « Ask Lia » visent LIA, la cloche du dock vise
 * NOTIFICATIONS. Fenetre fermee -> ouverte sur la cible ; ouverte sur une
 * autre source -> bascule sur la cible sans fermer ; deja sur la cible -> se
 * ferme, seul cas de bascule. Exception, la MASCOTTE (`reprendre`, P9 du
 * 06/10/2026) : fenetre fermee, elle rouvre la derniere conversation, quelle
 * que soit sa source (Lia, canal, direct, chatter).
 */
export function gesteDuLanceur(ouvert: boolean, source: SourceId, cible: SourceId, reprendre = false): 'ouvrir' | 'fermer' | 'reprendre' {
  if (ouvert) return source === cible ? 'fermer' : 'ouvrir';
  // La boite de notifications n'est pas une conversation : la mascotte rouvre alors Lia.
  return reprendre && source !== 'notifications' ? 'reprendre' : 'ouvrir';
}

/**
 * LE CHATTER D'UNE FICHE EST FOURNI PAR L'ECRAN (recopie de
 * `EVENEMENT_CHATTER_FICHE` et du registre de `@taki/app-runtime/chatter.ts`,
 * que le shell n'importe pas) : le bandeau « Fiche ouverte » n'existe que pour
 * une fiche dont le gabarit fournit le chatter (R10 ; base au chatter eteint :
 * pas de bandeau).
 */
export const EVENEMENT_CHATTER_FICHE = 'taki:chatter:fiche';

export function chattersDeFicheAnnonces(): ReadonlySet<string> {
  if (typeof window === 'undefined') return new Set();
  return new Set((window as unknown as { __takiChattersDeFiche?: Map<string, number> }).__takiChattersDeFiche?.keys() ?? []);
}
